import { format } from 'date-fns';
import type { Borders, Fill, Workbook } from 'exceljs';
import { AccountType } from 'further-types/investor';

export type ShareholdingAllocationRow = {
  tranche: string;
  accountType?: AccountType;
  registeredName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  subscriptionDate: string;
  sharesAllocated: number;
  capitalAllocated: number;
  capitalAllocatedAdjusted?: number;
  transactionFee?: number;
  transactionFeeVat?: number;
  deployableCapitalPre: number;
  deployableCapitalPost: number;
  finalDeal?: 'Yes' | '';
  claimingEis?: 'Yes' | 'No';
  externalInvestmentId: string;
  systemInvestmentId: string;
  companyId: string;
  trancheId: string;
  firmId: string;
};

const EDITABLE_CELL_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'F2F2F2' },
} as Fill;

const EDITABLE_CELL_BORDER = {
  top: { style: 'thin', color: { argb: 'D9D9D9' } },
  left: { style: 'thin', color: { argb: 'D9D9D9' } },
  bottom: { style: 'thin', color: { argb: 'D9D9D9' } },
  right: { style: 'thin', color: { argb: 'D9D9D9' } },
} as Borders;

export const buildShareholdingAllocationsSpreadsheet = async (
  companyName: string,
  shareClass: string,
  nominalValue: string,
  sharePrice: number,
  investmentDate: string,
  data: Array<ShareholdingAllocationRow>,
): Promise<Workbook> => {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  // Add header notes
  const noteRow1 = worksheet.addRow([
    'NOTE TO USER: COMPLETE GREY FIELDS ONLY (PLEASE DO NOT AMEND OTHER FIELDS)',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'System IDs (do not amend - used by system)',
    '',
    '',
    '',
    '',
  ]);

  // Merge the cells in the first row
  worksheet.mergeCells(`A1:N1`);
  worksheet.mergeCells(`O1:S1`);

  // Fill the first cell in green
  noteRow1.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'b2d1a9' },
  };
  // Make the first cell text bold
  noteRow1.getCell(1).font = { bold: true };

  // Fill the System IDs cell in blue
  noteRow1.getCell(15).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'a4c2f4' }, // Blue
  };

  worksheet.addRow([
    "(Note 1) if any Final deal? row equals 'Yes', this allocation will be treated as the final deal for that investment's entire corresponding tranche. Please leave column blank if not tranche's final deal.",
  ]);
  worksheet.addRow(['(Note 2) Each grey cell must be a value not a formula.']);
  worksheet.addRow([]);

  // Add company info
  worksheet.addRow(['Company', `${companyName} ${shareClass}`]);

  worksheet.addRow(['Share price', sharePrice]);
  const sharePriceCell = worksheet.getCell(`B${worksheet.rowCount}`);
  sharePriceCell.fill = EDITABLE_CELL_FILL;
  sharePriceCell.border = EDITABLE_CELL_BORDER;

  worksheet.addRow(['Nominal value', nominalValue]);
  const nominalValueCell = worksheet.getCell(`B${worksheet.rowCount}`);
  nominalValueCell.alignment = { horizontal: 'right' };

  worksheet.addRow(['Investment date', investmentDate]);
  const investmentDateCell = worksheet.getCell(`B${worksheet.rowCount}`);
  investmentDateCell.fill = EDITABLE_CELL_FILL;
  investmentDateCell.border = EDITABLE_CELL_BORDER;

  worksheet.addRow([
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'Use if G =/= Actual capital allocated',
    '',
    '',
    '',
    '',
    '',
    '',
  ]);

  // Add main headers
  worksheet.addRow([
    { richText: [{ text: 'Tranche', font: { bold: true } }] },
    { richText: [{ text: 'First name', font: { bold: true } }] },
    { richText: [{ text: 'Middle name', font: { bold: true } }] },
    {
      richText: [{ text: 'Last name', font: { bold: true } }],
    },
    { richText: [{ text: 'Subscription date', font: { bold: true } }] },
    { richText: [{ text: 'Shares allocated', font: { bold: true } }] },
    { richText: [{ text: 'Capital allocated', font: { bold: true } }] },
    {
      richText: [
        { text: 'Capital allocated adjusted (optional)', font: { bold: true } },
      ],
    },
    { richText: [{ text: 'Transaction fee (£)', font: { bold: true } }] },
    { richText: [{ text: 'Transaction fee VAT', font: { bold: true } }] },
    { richText: [{ text: 'Deployable capital (pre)', font: { bold: true } }] },
    { richText: [{ text: 'Deployable capital (post)', font: { bold: true } }] },
    { richText: [{ text: 'Final deal?', font: { bold: true } }] },
    { richText: [{ text: 'Claiming EIS', font: { bold: true } }] },
    { richText: [{ text: 'External subscription ID', font: { bold: true } }] },
    { richText: [{ text: 'System subscription ID', font: { bold: true } }] },
    { richText: [{ text: 'Company ID', font: { bold: true } }] },
    { richText: [{ text: 'Tranche ID', font: { bold: true } }] },
    { richText: [{ text: 'Firm ID', font: { bold: true } }] },
  ]);

  // Add data rows
  data.forEach((row) => {
    const nextRowNumber = worksheet.rowCount + 1;
    worksheet.addRow([
      row.tranche,
      row.firstName,
      row.middleName,
      row.accountType === AccountType.LegalEntity
        ? row.registeredName
        : row.lastName,
      format(new Date(row.subscriptionDate), 'dd-MM-yyyy'),
      row.sharesAllocated,
      { formula: `F${nextRowNumber}*$B$6` }, // Capital allocated = Shares * Share price
      row.capitalAllocatedAdjusted,
      row.transactionFee ?? 0,
      row.transactionFeeVat ?? 0,
      row.deployableCapitalPre,
      {
        formula: `K${nextRowNumber}-IF(ISBLANK(H${nextRowNumber}),G${nextRowNumber},H${nextRowNumber})-IF(ISBLANK(I${nextRowNumber}),0,I${nextRowNumber}*(1+IF(ISBLANK(J${nextRowNumber}),0,J${nextRowNumber})))`,
      }, // Deployable capital post = Pre - (Adjusted or Capital allocated) - Transaction fee including VAT
      row.finalDeal,
      row.claimingEis,
      row.externalInvestmentId,
      row.systemInvestmentId,
      row.companyId,
      row.trancheId,
      row.firmId,
    ]);
  });

  // Style the worksheet
  worksheet.getColumn(1).width = 20; // Tranche
  worksheet.getColumn(2).width = 20; // First name
  worksheet.getColumn(3).width = 20; // Middle name
  worksheet.getColumn(4).width = 20; // Last/corporate name
  worksheet.getColumn(5).width = 15; // Subscription date
  worksheet.getColumn(6).width = 15; // Shares allocated
  worksheet.getColumn(7).width = 15; // Capital allocated
  worksheet.getColumn(8).width = 35; // Capital allocated adjusted
  worksheet.getColumn(9).width = 20; // Transaction fee
  worksheet.getColumn(10).width = 20; // Transaction fee VAT
  worksheet.getColumn(11).width = 20; // Deployable capital (pre)
  worksheet.getColumn(12).width = 20; // Deployable capital (post)
  worksheet.getColumn(13).width = 15; // Final deal
  worksheet.getColumn(14).width = 15; // Claiming EIS
  worksheet.getColumn(15).width = 25; // External subscription ID
  worksheet.getColumn(16).width = 25; // System subscription ID
  worksheet.getColumn(17).width = 25; // Company ID
  worksheet.getColumn(18).width = 25; // Tranche ID
  worksheet.getColumn(19).width = 25; // Firm ID

  // Add data validation for Final deal? column
  const dataValidation = {
    type: 'list' as const,
    allowBlank: true,
    formulae: ['"Yes"'],
    showErrorMessage: true,
    errorStyle: 'error' as const,
    errorTitle: 'Invalid input',
    error: 'Please select "Yes" or leave blank',
  };

  // Apply validation to the Final deal? column
  for (let i = 10; i < worksheet.rowCount + 1; i++) {
    const cell = worksheet.getCell(`M${i}`);
    cell.dataValidation = dataValidation;
  }

  // Format percentage cells
  const percentageCells = [10]; // Columns Transaction fee VAT
  worksheet.eachRow((row, rowNumber) => {
    // Skip header rows
    if (rowNumber > 9) {
      percentageCells.forEach((col) => {
        const cell = row.getCell(col);
        cell.numFmt = '0.00%';
      });
    }
  });

  // Format currency cells in the current row
  const currencyCells = [7, 8, 9, 11, 12]; // Columns Capital allocated, Capital allocated adjusted, Transaction fee, Deployable capital (pre), Deployable capital (post)
  worksheet.eachRow((row, rowNumber) => {
    // Skip header rows
    if (rowNumber > 9) {
      currencyCells.forEach((col) => {
        const cell = row.getCell(col);
        cell.numFmt = '"£"#,##0.00';
      });
    }
  });

  // Set editable cells (grey fields)
  const editableColumns = [6, 8, 9, 10, 13]; // Shares allocated, Capital allocated adjusted, Transaction fee, Transaction fee VAT, Final deal?
  worksheet.eachRow((row, rowNumber) => {
    // Skip header rows
    if (rowNumber > 9) {
      editableColumns.forEach((colNumber) => {
        const cell = row.getCell(colNumber);
        cell.fill = EDITABLE_CELL_FILL;
        cell.border = EDITABLE_CELL_BORDER;
      });
    }
  });

  return workbook;
};
